import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Calendar, MessageSquare, Check, X, Eye, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact/list`);
      setInquiries(response.data);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast({
        title: "Error",
        description: "Failed to load inquiries",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/contact/${inquiryId}/status`, { status: newStatus });
      toast({
        title: "Status Updated",
        description: "Inquiry status has been updated successfully"
      });
      fetchInquiries();
      if (selectedInquiry?.id === inquiryId) {
        const updated = await axios.get(`${API}/contact/list`);
        setSelectedInquiry(updated.data.find(i => i.id === inquiryId));
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const addNote = async (inquiryId) => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API}/contact/${inquiryId}/notes`, { note: newNote });
      setNewNote('');
      toast({
        title: "Note Added",
        description: "Your note has been added successfully"
      });
      fetchInquiries();
      const updated = await axios.get(`${API}/contact/list`);
      setSelectedInquiry(updated.data.find(i => i.id === inquiryId));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: { bg: 'bg-blue-100 text-blue-800', label: 'New' },
      read: { bg: 'bg-yellow-100 text-yellow-800', label: 'Read' },
      responded: { bg: 'bg-green-100 text-green-800', label: 'Responded' },
      closed: { bg: 'bg-gray-100 text-gray-800', label: 'Closed' }
    };
    
    const variant = variants[status] || variants.new;
    return <Badge className={variant.bg}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Loading inquiries...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button onClick={() => navigate('/')} variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Website
            </Button>
            <h1 className="text-4xl font-bold text-navy-900">Contact Inquiries</h1>
            <p className="text-gray-600 mt-2">Manage and respond to customer inquiries</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-burgundy-700">{inquiries.length}</p>
            <p className="text-sm text-gray-600">Total Inquiries</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {['new', 'read', 'responded', 'closed'].map((status) => (
            <Card key={status}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-navy-900">
                    {inquiries.filter(i => i.status === status).length}
                  </p>
                  <p className="text-sm text-gray-600 capitalize mt-1">{status}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Inquiries List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-navy-900">All Inquiries</h2>
            {inquiries.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">No inquiries yet</p>
                </CardContent>
              </Card>
            ) : (
              inquiries.map((inquiry) => (
                <Card 
                  key={inquiry.id}
                  className={`cursor-pointer transition-all hover:shadow-lg ${
                    selectedInquiry?.id === inquiry.id ? 'ring-2 ring-burgundy-700' : ''
                  }`}
                  onClick={() => setSelectedInquiry(inquiry)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{inquiry.name}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Mail className="w-4 h-4" />
                          {inquiry.email}
                        </CardDescription>
                      </div>
                      {getStatusBadge(inquiry.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{inquiry.message}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(inquiry.created_at).toLocaleDateString()}
                      </span>
                      {inquiry.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {inquiry.phone}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Inquiry Details */}
          <div className="sticky top-8">
            {selectedInquiry ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl">{selectedInquiry.name}</CardTitle>
                      <CardDescription className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${selectedInquiry.email}`} className="hover:text-burgundy-700">
                            {selectedInquiry.email}
                          </a>
                        </div>
                        {selectedInquiry.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${selectedInquiry.phone}`} className="hover:text-burgundy-700">
                              {selectedInquiry.phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(selectedInquiry.created_at).toLocaleString()}
                        </div>
                      </CardDescription>
                    </div>
                    {getStatusBadge(selectedInquiry.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Message */}
                  <div>
                    <h3 className="font-semibold mb-2">Message:</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedInquiry.message}</p>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div>
                    <label className="font-semibold mb-2 block">Update Status:</label>
                    <Select 
                      value={selectedInquiry.status}
                      onValueChange={(value) => updateStatus(selectedInquiry.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                        <SelectItem value="responded">Responded</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Notes */}
                  {selectedInquiry.notes && selectedInquiry.notes.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Notes:</h3>
                      <div className="space-y-2">
                        {selectedInquiry.notes.map((note, index) => (
                          <div key={index} className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                            <p className="text-sm text-gray-700">{note.note}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(note.added_at).toLocaleString()} - {note.added_by}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Note */}
                  <div>
                    <label className="font-semibold mb-2 block">Add Note:</label>
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add internal notes or follow-up actions..."
                      rows={3}
                      className="mb-2"
                    />
                    <Button 
                      onClick={() => addNote(selectedInquiry.id)}
                      className="w-full bg-burgundy-700 hover:bg-burgundy-800"
                      disabled={!newNote.trim()}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => window.location.href = `mailto:${selectedInquiry.email}`}
                      className="flex-1 bg-navy-700 hover:bg-navy-800"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Reply via Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-24 text-center">
                  <Eye className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Select an inquiry to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
